import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './DocumentManagerTab.scss';

const DocumentManagerTab = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      // Access the documentsManager subcollection
      const documentsManagerDocRef = doc(db, 'otherDBs', 'documentsManager');
      const documentsManagerCollectionRef = collection(documentsManagerDocRef, 'documentsManager');
      const querySnapshot = await getDocs(documentsManagerCollectionRef);
      
      const documentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDocuments(documentsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all documents
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all documents? This will update ${documents.length} records.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find documents that need migration
      const documentsToMigrate = documents.filter(document => {
        // Skip already migrated documents
        if (document.migrated) return false;
        
        // Check if either employeeEmail or uploadedBy have mappings
        const hasEmployeeEmailMapping = document.employeeEmail && findNewEmail(document.employeeEmail);
        const hasUploadedByMapping = document.uploadedBy && findNewEmail(document.uploadedBy);
        
        return hasEmployeeEmailMapping || hasUploadedByMapping;
      });
      
      setProgress({ current: 0, total: documentsToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process documents in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < documentsToMigrate.length; i++) {
        const document = documentsToMigrate[i];
        const oldEmployeeEmail = document.employeeEmail;
        const oldUploadedBy = document.uploadedBy;
        
        const newEmployeeEmail = oldEmployeeEmail ? findNewEmail(oldEmployeeEmail) : null;
        const newUploadedBy = oldUploadedBy ? findNewEmail(oldUploadedBy) : null;
        
        if (newEmployeeEmail || newUploadedBy) {
          const documentRef = doc(db, 'otherDBs', 'documentsManager', 'documentsManager', document.id);
          
          const updateData = {
            migrated: true,
            migratedAt: new Date()
          };
          
          if (newEmployeeEmail) {
            updateData.employeeEmail = newEmployeeEmail;
            updateData.oldEmployeeEmail = oldEmployeeEmail;
          }
          
          if (newUploadedBy) {
            updateData.uploadedBy = newUploadedBy;
            updateData.oldUploadedBy = oldUploadedBy;
          }
          
          batch.update(documentRef, updateData);
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: documentsToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} documents.`);
      
      // Refresh the list
      fetchDocuments();
      
    } catch (error) {
      console.error('Error migrating documents:', error);
      alert(`Error migrating documents: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredDocuments = searchTerm 
    ? documents.filter(document => 
        document.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.employeeEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.uploadedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        document.fileRefName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : documents;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Get filename from URL
  const getFileName = (fileRefName, fileURL) => {
    if (fileRefName) return fileRefName;
    if (!fileURL) return 'N/A';
    
    try {
      // Extract filename from Firebase Storage URL
      const urlParts = fileURL.split('/');
      const fileWithParams = urlParts[urlParts.length - 1];
      const fileName = fileWithParams.split('?')[0];
      // Decode URI component to handle special characters
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileURL.substring(fileURL.lastIndexOf('/') + 1, fileURL.indexOf('?'));
    }
  };

  // Count total number of migratable documents
  const getMigratableCounts = () => {
    const total = documents.length;
    const alreadyMigrated = documents.filter(document => document.migrated).length;
    const canBeMigrated = documents.filter(document => {
      if (document.migrated) return false;
      
      const hasEmployeeEmailMapping = document.employeeEmail && findNewEmail(document.employeeEmail);
      const hasUploadedByMapping = document.uploadedBy && findNewEmail(document.uploadedBy);
      
      return hasEmployeeEmailMapping || hasUploadedByMapping;
    }).length;
    
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  return (
    <div className="document-manager-tab">
      <div className="documents-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name, email, category, or filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchDocuments}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Documents:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating ? 'Migrating...' : 'Migrate All Documents'}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} documents...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} documents in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading documents...</p>
        </div>
      ) : filteredDocuments.length > 0 ? (
        <div className="documents-table-container">
          <table className="documents-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>File</th>
                <th>Employee Email</th>
                <th>Uploaded By</th>
                <th>Created At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map(document => (
                <tr key={document.id} className={document.migrated ? 'migrated-row' : ''}>
                  <td>{document.name || 'N/A'}</td>
                  <td>{document.category || 'N/A'}</td>
                  <td className="file-cell">
                    {document.fileURL ? (
                      <a href={document.fileURL} target="_blank" rel="noopener noreferrer">
                        {getFileName(document.fileRefName, document.fileURL)}
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {document.employeeEmail || 'N/A'}
                    {document.migrated && document.oldEmployeeEmail && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {document.oldEmployeeEmail}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    {document.uploadedBy || 'N/A'}
                    {document.migrated && document.oldUploadedBy && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {document.oldUploadedBy}</div>
                      </div>
                    )}
                  </td>
                  <td>{formatDate(document.createdAt)}</td>
                  <td>
                    {document.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : (findNewEmail(document.employeeEmail) || findNewEmail(document.uploadedBy)) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No documents found.</p>
        </div>
      )}
    </div>
  );
};

export default DocumentManagerTab; 